import styled, {keyframes} from 'styled-components';
import format from 'date-fns/format';
import {t} from 'i18next';
import {css} from 'styled-components';
import IconReverse from '../../assets/images/icon-reverse.svg';
import IconBlank from '../../assets/images/icon-blank.svg';

const formatFullName = (firstName?: string, lastName?: string) => {
  let fullName;
  if (firstName && lastName) {
    fullName =
      firstName[0].toUpperCase() +
      firstName.substring(1).toLowerCase() +
      ' ' +
      lastName[0].toUpperCase() +
      lastName.substring(1).toLowerCase();

    return fullName;
  }

  return '';
};

type TableProps = {
  experiences: HRExperience[];
  loading: boolean;
  error: boolean;
  isReachingEnd?: boolean;
  isEmpty?: boolean;
  orderBy: 'lastName' | 'email' | 'lastAccess';
  ordering: 'desc' | 'asc';
  isSharedOne?: boolean;
  loadMore: () => void;
  onOrderByChange: (o: TableProps['orderBy']) => void;
  onOrderingChange: () => void;
};

type RowProps = {
  element: HRExperience;
  index: number;
};

const PlaysTable = ({
  experiences,
  loading,
  error,
  isReachingEnd,
  isEmpty,
  loadMore,
  orderBy,
  ordering,
  isSharedOne,
  onOrderByChange,
  onOrderingChange,
}: TableProps) => {
  const Row = ({element}: RowProps) => {
    const reportUrl =
      element.reportGenerate && element.reportUrl ? element.reportUrl : null;
    const reportBadgeUrl =
      element.reportBadgeGenerate && element.reportBadgeUrl
        ? element.reportBadgeUrl
        : null;

    return (
      <TableRow>
        <TableData>
          {formatFullName(
            element.participant.firstName,
            element.participant.lastName
          )}
        </TableData>
        <TableData>{element.participant.email}</TableData>
        <TableData>
          {element.status === 'completed' ? (
            <strong>{t(`dashboard.status.${element.status}`)}</strong>
          ) : (
            t(`dashboard.status.${element.status}`)
          )}
        </TableData>
        <TableData>
          {isSharedOne
            ? element.project.name
            : format(new Date(element.lastAccess), 'dd/MM/yyyy')}
        </TableData>
        <TableData>
          {reportUrl || reportBadgeUrl ? (
            <ReportActions>
              {reportUrl && (
                <ReportLink href={reportUrl} target="_blank" rel="noreferrer">
                  <span>{t('dashboard.go-to-report')}</span>
                  <Icon image={IconBlank} />
                </ReportLink>
              )}
              {reportBadgeUrl && (
                <ReportLink
                  href={reportBadgeUrl}
                  target="_blank"
                  rel="noreferrer"
                >
                  <span>{t('dashboard.go-to-skill-badge')}</span>
                  <Icon image={IconBlank} />
                </ReportLink>
              )}
            </ReportActions>
          ) : (
            '---'
          )}
        </TableData>
      </TableRow>
    );
  };

  const List = () => {
    if (error) {
      return <EmptyMessage>{t('dashboard.list-error')}</EmptyMessage>;
    }

    if (isEmpty) {
      return <EmptyMessage>{t('dashboard.empty')}</EmptyMessage>;
    }

    return experiences?.map((el, index: number) => {
      return <Row key={index} element={el} index={index} />;
    });
  };

  return (
    <div style={{position: 'relative', paddingBottom: '80px'}}>
      <TableContainer>
        <Table>
          <TableHeadRow>
            <TableHead active={orderBy === 'lastName'}>
              <button onClick={() => onOrderByChange('lastName')}>
                {t('dashboard.player-name')}
              </button>
              <button onClick={onOrderingChange}>
                <IconReverseImg src={IconReverse} />
              </button>
            </TableHead>
            <TableHead active={orderBy === 'email'}>
              <button onClick={() => onOrderByChange('email')}>
                {t('dashboard.play-email')}
              </button>
              <button onClick={onOrderingChange}>
                <IconReverseImg src={IconReverse} />
              </button>
            </TableHead>
            <TableHead>{t('dashboard.play-status')}</TableHead>
            {isSharedOne ? (
              <TableHead>{t('dashboard.project')}</TableHead>
            ) : (
              <TableHead active={orderBy === 'lastAccess'}>
                <button onClick={() => onOrderByChange('lastAccess')}>
                  {t('dashboard.play-last-access')}
                </button>
                <button onClick={onOrderingChange}>
                  <IconReverseImg src={IconReverse} />
                </button>
              </TableHead>
            )}
            <TableHead>{t('dashboard.report')}</TableHead>
          </TableHeadRow>
          {loading && (
            <ListLoading>
              <ListLoader />
            </ListLoading>
          )}
          <ListContainer>{List()}</ListContainer>
        </Table>
      </TableContainer>
      {!error && !loading && !isReachingEnd && experiences !== undefined && (
        <Pagination>
          <PaginationButton disabled={isReachingEnd} onClick={loadMore}>
            {t('dashboard.loadmore')}
          </PaginationButton>
        </Pagination>
      )}
    </div>
  );
};

export default PlaysTable;

const TableContainer = styled.div`
  width: 100%;
  overflow: auto;
`;

const ListContainer = styled.div`
  overflow: auto;
  height: 400px;
  background-color: rgba(0, 0, 0, 0.02);
  border-radius: 8px;
`;

const Table = styled.div`
  min-width: 960px;
  position: relative;
`;

const TableRow = styled.div`
  display: flex;
  gap: 16px;
  width: 100%;
  border-radius: 4px;
  padding: 16px 20px;
  background-color: ${(p) => p.theme.colors.violet100};

  & + & {
    margin-top: 8px;
  }
`;

const TableHeadRow = styled(TableRow)`
  margin-bottom: 16px;
  background-color: ${(p) => p.theme.colors.orange100};
`;

const TableData = styled.div`
  flex-basis: calc(100% / 5);
  text-align: left;
  font-size: 13px;
  font-family: 'Bossa', sans-serif;
  word-break: break-all;
  padding: 2px 4px;

  strong {
    color: ${(p) => p.theme.colors.violet500};
  }
`;

const TableHead = styled(TableData)<{active?: boolean}>`
  display: inline-flex;
  justify-content: flex-start;
  gap: 4px;
  color: ${(p) => p.theme.colors.orange600};
  font-weight: 300;
  font-size: 13px;

  ${(p) =>
    p.active &&
    css`
      font-weight: 700;
    `}

  button {
    all: unset;
    cursor: pointer;
  }
`;

const Pagination = styled.div`
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 24px;
`;

const PaginationButton = styled.button.attrs({
  type: 'button',
})`
  margin: 0;
  padding: 10px;
  border: none;
  background-color: transparent;
  text-align: center;
  font-family: 'Bossa', sans-serif;
  color: ${(p) => p.theme.colors.orange600};

  &:disabled {
    opacity: 0.5;
  }

  &:hover:not(:disabled) {
    text-decoration: underline;
    cursor: pointer;
  }
`;

const ListLoading = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding-top: 140px;

  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;

  @media ${(props) => props.theme.breakpoints.tablet} {
    justify-content: flex-start;
    padding-left: 50px;
  }
`;

const rotate = keyframes`
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
`;

const ListLoader = styled.div`
  width: 32px;
  height: 32px;
  border: solid 4px ${(p) => p.theme.colors.orange600};
  border-bottom-color: transparent;
  border-radius: 50%;
  background-color: transparent;
  animation: ${rotate} infinite linear 800ms;
`;

const EmptyMessage = styled.h4`
  margin: 24px auto 0;
  text-align: center;
  font-weight: 400;
  font-family: 'Bossa', sans-serif;
`;

const Icon = styled.span<{image?: string}>`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  background-position: center;
  background-repeat: no-repeat;
  background-size: 100%;
  ${(p) => `background-image: url(${p.image})`};
`;

const ReportLink = styled.a`
  display: inline-flex;
  align-items: center;

  span {
    margin-right: 6px;
    white-space: nowrap;
  }
`;

const ReportActions = styled.div`
  display: inline-flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
`;

const IconReverseImg = styled.img`
  display: block;
  margin: 0;
  width: 16px;
  height: 16px;
`;
